import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'

import { getContractRoom } from './get-contract-room'
import {
  FavoriteRoomsSearchParams,
  getFavoriteRooms,
} from './get-favorite-rooms'
import {
  getHostRoomSchedules,
  HostRoomSchedulesParams,
} from './get-host-room-schedules'
import { getHostRooms, HostRoomsSearchParams } from './get-host-rooms'
import {
  getRecentViewedRooms,
  getRecentViewedRoomsWithPagination,
  RecentViewedRoomsSearchParams,
} from './get-recent-viewed-rooms'
import { getRoom } from './get-room'
import { getRoomAvailableWeeks } from './get-room-available-weeks'
import { getRoomRecommendSearchKeywords } from './get-room-recommend-search-keywords'
import { getRoomSchedules, RoomSchedulesParams } from './get-room-schedules'
import { getRooms } from './get-rooms'
import { TRoomsSearchParams } from './get-rooms/schema'
import { getRoomsCount } from './get-rooms-count'
import { TRoomsCountSearchParams } from './get-rooms-count/schema'
import {
  getSurveyRequirements,
  SurveyRequirementsParams,
} from './get-survey-requirements'

export const roomQueries = {
  all: () => ['rooms'],
  roomLists: () => [...roomQueries.all(), 'list'],
  roomList: (params: TRoomsSearchParams) =>
    infiniteQueryOptions({
      queryKey: [...roomQueries.roomLists(), params],
      queryFn: ({ pageParam }) => getRooms({ ...params, page: pageParam }),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages, lastPageParam) => {
        if (lastPage.rooms.last) return undefined

        return lastPageParam + 1
      },
    }),
  roomsCount: (params: TRoomsCountSearchParams) =>
    queryOptions({
      queryKey: [...roomQueries.roomLists(), 'count', params],
      queryFn: () => getRoomsCount(params),
    }),
  room: ({ roomId }: { roomId: number }) =>
    queryOptions({
      queryKey: [...roomQueries.all(), roomId],
      queryFn: () => getRoom({ roomId }),
    }),
  contractRoom: ({
    roomId,
    contractId,
  }: {
    roomId: number
    contractId: number
  }) =>
    queryOptions({
      queryKey: [...roomQueries.all(), roomId, contractId],
      queryFn: () => getContractRoom({ roomId, contractId }),
    }),
  recentViewedRooms: () =>
    queryOptions({
      queryKey: [...roomQueries.all(), 'recent-viewed'],
      queryFn: () => getRecentViewedRooms(),
    }),
  recentViewedRoomsWithPagination: (params: RecentViewedRoomsSearchParams) =>
    queryOptions({
      queryKey: [...roomQueries.all(), 'recent-viewed', params],
      queryFn: () => getRecentViewedRoomsWithPagination(params),
    }),
  favoriteRooms: (params: FavoriteRoomsSearchParams) =>
    queryOptions({
      queryKey: [...roomQueries.all(), 'favorite', params],
      queryFn: () => getFavoriteRooms(params),
    }),
  roomAvailableWeeks: (params: {
    roomId: number
    startDate: string
    isExtend?: boolean
  }) =>
    queryOptions({
      queryKey: [...roomQueries.all(), 'available-weeks', params],
      queryFn: () => getRoomAvailableWeeks(params),
    }),
  roomSchedules: (params: RoomSchedulesParams) =>
    queryOptions({
      queryKey: [...roomQueries.all(), 'schedules', params],
      queryFn: () => getRoomSchedules(params),
    }),
  hostRooms: (params: HostRoomsSearchParams) =>
    queryOptions({
      queryKey: [...roomQueries.all(), 'host-rooms', params],
      queryFn: () => getHostRooms(params),
    }),
  hostRoomSchedules: (params: HostRoomSchedulesParams) =>
    queryOptions({
      queryKey: [...roomQueries.all(), 'schedules', params],
      queryFn: () => getHostRoomSchedules(params),
    }),
  surveyRequirements: (params: SurveyRequirementsParams) =>
    queryOptions({
      queryKey: [...roomQueries.all(), 'survey-requirements', params],
      queryFn: () => getSurveyRequirements(params),
    }),
  recommendSearchKeywords: () =>
    queryOptions({
      queryKey: [...roomQueries.all(), 'recommend-search-keywords'],
      queryFn: () => getRoomRecommendSearchKeywords(),
    }),
}
